/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import {
  Box,
  ClickAwayListener,
  IconButton,
  TooltipProps,
} from '@mui/material';
import { SxProps } from '@mui/material';
import Popper, { PopperPlacementType } from '@mui/material/Popper';
import { useTheme } from '@mui/material/styles';
import useIsMobile from 'hooks/useIsMobile';
import TuneIcon from '@mui/icons-material/Tune';
import { useRef, useState } from 'react';
import { SGTooltip } from '../core';
import { SGModal, SGModalProps } from './SGModal';

interface SettingsPopoutProps {
  title: string;
  popperID: string;
  children: React.ReactNode | React.ReactNode[];
  sx?: SxProps;
  placement?: PopperPlacementType;
  tooltipPlacement?: TooltipProps['placement'];
  externalMenuOpen?: boolean;
  icon?: any;
  zIndex?: number;
  useModal?: boolean;
  modalProps?: Partial<SGModalProps>;
  disablePortal?: boolean;
}

const DEFAULT_STYLES: SxProps = {
  width: '350px',
  height: 'auto',
  marginTop: '10px',
  padding: '16px',
  textAlign: 'center',
};

export const SettingsPopout = ({
  children,
  placement = 'auto',
  popperID,
  sx = {},
  title,
  tooltipPlacement = 'bottom',
  externalMenuOpen,
  useModal,
  modalProps,
  icon = <TuneIcon />,
  zIndex = 10_000,
  disablePortal,
}: SettingsPopoutProps) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [openSettings, setOpenSettings] = useState(false);

  const defaultStyle: SxProps = {
    ...DEFAULT_STYLES,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.palette.shadows.default,
    borderRadius: theme.spacing(3),
  };

  const handleClose = () => {
    if (externalMenuOpen) {
      return;
    }
    setOpenSettings(false);
  };

  const body = <Box sx={{ ...defaultStyle, ...sx }}>{children}</Box>;

  return (
    <>
      <IconButton
        className="canvas-no-drag"
        aria-label={title}
        ref={anchorRef}
        onClick={() => {
          setOpenSettings((previousOpen) => !previousOpen);
        }}
        size="medium"
        color="primary"
      >
        <SGTooltip title={title} placement={tooltipPlacement}>
          {icon}
        </SGTooltip>
      </IconButton>
      {useModal && (
        <SGModal
          open={openSettings}
          onClose={handleClose}
          {...(modalProps ?? {})}
        >
          {body}
        </SGModal>
      )}
      {!useModal && (
        <Popper
          className="canvas-no-drag"
          id={popperID}
          open={openSettings}
          anchorEl={anchorRef.current}
          placement={placement}
          disablePortal={disablePortal ?? !isMobile}
          sx={{ ...(!isMobile && { zIndex }) }}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <div role="presentation">{body}</div>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};

export default SettingsPopout;
